/** Envoi devis / facture / avoir : texte de ce courriel à gauche, pièce à droite. */
import { useEffect, useState } from "react";
import { Button } from "../../components/Button";
import { Field } from "../../components/Field";
import { RichText } from "../../components/RichText";
import { useCompany } from "../../company/CompanyProvider";
import { fillMailText, mailFromCompany, type MailKind } from "../../email/types";
import { useFormatters } from "../../formats/useFormatters";
import { quotePrintUrl } from "./quotePdf";
import type { DocKind, Quote } from "./types";
import "./quotes.css";

type SendMailSheetProps = {
  doc: Quote;
  clientName: string;
  clientEmail: string;
  busy: boolean;
  error: string;
  onCancel: () => void;
  onSend: (mail: { subject: string; body: string }) => void;
};

const TITLES: Record<DocKind, string> = {
  quote: "Envoyer ce devis",
  invoice: "Envoyer cette facture",
  credit_note: "Envoyer cet avoir",
};

function mailKind(kind: string): MailKind {
  if (kind === "quote" || kind === "credit_note") return kind;
  return "invoice";
}

export function SendMailSheet({ doc, clientName, clientEmail, busy, error, onCancel, onSend }: SendMailSheetProps) {
  const { company } = useCompany();
  const { money, day } = useFormatters();
  const kind = mailKind(doc.doc_type);
  const piece: DocKind = doc.doc_type === "quote" || doc.doc_type === "credit_note" ? doc.doc_type : "invoice";
  const draft = doc.status === "draft";
  const numero = (doc.number || "").trim();
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");

  useEffect(() => {
    const mail = mailFromCompany(company);
    const vars = {
      client: clientName || "Client",
      numero: numero || "{numero}",
      montant: money(doc.total_ttc_cents || 0),
      societe: company?.legal_name || "",
      bouton: "{bouton}",
      date: doc.issue_date ? day(doc.issue_date) : "",
      echeance: doc.due_date ? day(doc.due_date) : "",
    };
    const tpl = mail.templates[kind];
    setSubject(fillMailText(tpl.subject, vars));
    setBody(fillMailText(tpl.body, vars));
  }, [company, clientName, numero, kind, doc.total_ttc_cents, doc.issue_date, doc.due_date, money, day]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onCancel();
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [busy, onCancel]);

  const title = TITLES[piece];
  const canSend = Boolean(clientEmail) && subject.trim() !== "" && body.trim() !== "";

  return (
    <div className="fx-modal fx-send" role="dialog" aria-modal="true" aria-labelledby="fx-send-title">
      <button type="button" className="fx-modal__backdrop" aria-label="Fermer" disabled={busy} onClick={onCancel} />
      <div className="fx-modal__card fx-send__card">
        <header className="fx-send__head">
          <h2 id="fx-send-title" className="fx-modal__title">
            {title}
          </h2>
          <p>
            {draft
              ? "L’envoi numérote et fige la pièce, puis part le courriel. Le texte modifié ne change que cet envoi."
              : "Le texte modifié ne change que cet envoi. Le modèle des paramètres reste le même."}
          </p>
        </header>
        <div className="fx-send__split">
          <div className="fx-send__mail">
            <dl className="fx-send__who">
              <div>
                <dt>De</dt>
                <dd>{mailFromCompany(company).from_name.trim() || company?.legal_name || "—"}</dd>
              </div>
              <div>
                <dt>À</dt>
                <dd>{clientEmail || "Le client n’a pas d’e-mail."}</dd>
              </div>
            </dl>
            <Field
              label="Objet"
              value={subject}
              disabled={busy}
              onChange={(e) => setSubject(e.target.value.slice(0, 180))}
            />
            <RichText
              label="Texte"
              compact
              fonts={false}
              disabled={busy}
              value={body}
              resetKey={doc.id}
              onChange={setBody}
            />
            <p className="fx-send__hint">
              {numero
                ? "{bouton} devient le lien vers la pièce."
                : "{numero} sera le numéro posé à l’envoi. {bouton} devient le lien vers la pièce."}
            </p>
            {error ? <p className="fx-quote-flash is-err">{error}</p> : null}
          </div>
          <div className="fx-send__pdf">
            <p>Aperçu de la pièce</p>
            <iframe title={title} src={quotePrintUrl(doc.id)} />
          </div>
        </div>
        <div className="fx-modal__actions">
          <Button variant="ghost" disabled={busy} onClick={onCancel}>
            Annuler
          </Button>
          <Button disabled={busy || !canSend} onClick={() => onSend({ subject: subject.trim(), body })}>
            {busy ? "Envoi…" : "Envoyer"}
          </Button>
        </div>
      </div>
    </div>
  );
}
